/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import { Stack, Switch } from '@mui/material';
import { useQueryClient } from 'react-query';
import { capitalize, format, formatCurrency, formatISODate } from 'common/utils/common.util';
import { Link } from 'react-router-dom';
import { ROUTES } from 'common/constants/urls';
import { HEIGHTS } from '../report/constants';
import { CCReportComp, DeleteCCReportCompsDTO } from './construction-cost-report.type';
import { Launch } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Box, Typography } from 'components/index';
import { Delete as DeleteIcon } from '@mui/icons-material';
import WhiteFab from '../../components/common/WhiteFab';
import { usePrompt } from '../../components/AppPrompt';
import { deleteCCReportComps } from './construction-cost-report.api';
import { handleError } from 'common/utils/util.errors';
import keys from 'common/react-query/data-keys';
import { ReportTypes } from 'common/utils/util.type';
import { CustomTooltip } from '../common/CustomTooltip';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Row } from 'material-react-table';
import { GridFilterModel } from '@mui/x-data-grid';

interface EditCCCompsSectionProps {
  comps: CCReportComp[];
  updateComps: (updatedComps: CCReportComp[]) => void;
  reportId: string;
  isReportDirty: boolean;
  typeofReport: ReportTypes;
  showMap: boolean;
  includeSoftCost: boolean;
  onTableReady?: (table: any, columnVisibility: Record<string, boolean>) => void;
  onColumnVisibilityChange?: (columnVisibility: Record<string, boolean>) => void;
  filterModel?: GridFilterModel;
}

const EditCCCompsSection: React.FC<EditCCCompsSectionProps> = ({
  comps,
  updateComps,
  reportId,
  isReportDirty,
  typeofReport,
  showMap,
  includeSoftCost,
  onTableReady,
  onColumnVisibilityChange,
  filterModel,
}) => {
  const queryClient = useQueryClient();
  const { getConfirmation } = usePrompt();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({
    'comp.grossBuildingArea': false,
    'comp.netArea': false,
    'comp.unitCount': false,
  });
  const isReportDirtyRef = useRef(isReportDirty);

  useEffect(() => {
    isReportDirtyRef.current = isReportDirty;
  }, [isReportDirty]);

  // Apply filters to comps
  const filteredComps = React.useMemo(() => {
    if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
      return comps;
    }

    return comps.filter((comp: CCReportComp) => {
      return filterModel.items.every(filterItem => {
        const { field, operator, value } = filterItem;
        if (!value && value !== 0) return true;

        // Get the nested property value
        const getValue = (obj: any, path: string) => {
          return path.split('.').reduce((current, prop) => current?.[prop], obj);
        };

        const fieldValue = getValue(comp, field);
        if (fieldValue === undefined || fieldValue === null) return false;

        // Convert to string for string comparisons
        const fieldValueStr = String(fieldValue).toLowerCase();
        const valueStr = String(value).toLowerCase();

        switch (operator) {
          case 'contains':
            return fieldValueStr.includes(valueStr);
          case 'equals':
            return fieldValue === value || fieldValueStr === valueStr;
          case 'startsWith':
            return fieldValueStr.startsWith(valueStr);
          case 'endsWith':
            return fieldValueStr.endsWith(valueStr);
          case 'isEmpty':
            return !fieldValue || fieldValueStr === '';
          case 'isNotEmpty':
            return fieldValue && fieldValueStr !== '';
          case '>':
            return Number(fieldValue) > Number(value);
          case '>=':
            return Number(fieldValue) >= Number(value);
          case '<':
            return Number(fieldValue) < Number(value);
          case '<=':
            return Number(fieldValue) <= Number(value);
          case '=':
            return Number(fieldValue) === Number(value);
          case '!=':
            return Number(fieldValue) !== Number(value);
          default:
            return true;
        }
      });
    });
  }, [comps, filterModel]);

  const handleIncludedCompsChange = React.useCallback(
    (compId: string, value: boolean) => {
      const updatedComps = comps.map(comp => {
        if (comp.comp.id === compId) {
          return { ...comp, isIncluded: value };
        }
        return comp;
      });
      const includedCompsAfterUpdate = updatedComps.filter(comp => comp.isIncluded);
      if (includedCompsAfterUpdate.length === 0) {
        toast.error('At least one comparable must be selected');
        return;
      }
      updateComps(updatedComps);
    },
    [comps, updateComps],
  );

  const handleDelete = React.useCallback(
    async (report: CCReportComp) => {
      if (comps.length === 1) {
        toast.error('At least one comparable must be selected');
        return;
      }
      const isConfirmed = await getConfirmation({
        heading: 'Delete Comparable',
        description: isReportDirtyRef.current
          ? `Deleting this comparable '${report.comp.projectTitle}' will overwrite any unsaved changes in the report. Are you sure you want to continue?`
          : `Are you sure you want to delete this comparable: ${report.comp.projectTitle}?`,
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return;
      try {
        setIsDeleting(true);
        const dto: DeleteCCReportCompsDTO = {
          reportId: reportId,
          CompIdsToDelete: [report.comp.id],
        };
        await deleteCCReportComps(dto);
        toast.success('Comparable deleted successfully');
        queryClient.invalidateQueries([keys.ccReport, reportId]);
      } catch (error) {
        handleError(error);
      }
      setIsDeleting(false);
    },
    [getConfirmation, reportId, queryClient, comps],
  );

  const columns = React.useMemo<MRT_ColumnDef<CCReportComp>[]>(
    () => [
      {
        accessorKey: 'isIncluded',
        header: 'Selected',
        size: 80,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => (
          <Switch
            checked={row.original.isIncluded}
            onChange={event => handleIncludedCompsChange(row.original.comp.id, event.target.checked)}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        enablePinning: true, // Pin this column
        muiTableHeadCellProps: {
          align: 'center' as const,
        },
        muiTableBodyCellProps: {
          align: 'center' as const,
        },
      },
      {
        accessorKey: 'comp.projectTitle',
        header: 'Project',
        size: 200,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const comp = row.original.comp;
          if (!comp) return '--';
          return (
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              p={0}
              sx={{
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'unset',
                cursor: 'pointer',
              }}
            >
              <Link to={`${ROUTES.PROJECTS}/${comp.id}`} target='_blank' style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CustomTooltip title={comp.projectTitle}>
                  <Typography
                    display='inline-block'
                    fontWeight='light'
                    fontSize='medium'
                    noWrap
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {comp.projectTitle}
                  </Typography>
                </CustomTooltip>
                <Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />
              </Link>
            </Stack>
          );
        },
        enableSorting: false,
        enablePinning: true, // Pin this column
        muiTableHeadCellProps: {
          align: 'left' as const,
        },
        muiTableBodyCellProps: {
          align: 'left' as const,
        },
      },
      {
        accessorKey: 'totalBudget',
        header: 'Total Budget',
        size: 120,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const value = includeSoftCost ? row.original.comp.totalBudgetWithSoftCost : row.original.comp.totalBudgetWithoutSoftCost;
          return value !== undefined ? formatCurrency(value.toString(), true, true) : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'comp.maxPeriodEnding',
        header: 'Budget Date',
        size: 120,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => formatISODate(row.original.comp.maxPeriodEnding ?? '--'),
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'costPerGSF',
        header: 'Cost per GSF',
        size: 110,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const area = row.original.comp.grossBuildingArea ?? 1;
          const budgetWithSoftCost = row.original.comp.totalBudgetWithSoftCost;
          const budgetWithoutSoftCost = row.original.comp.totalBudgetWithoutSoftCost;
          const value = row.original.comp.grossBuildingArea
            ? (includeSoftCost ? (budgetWithSoftCost ?? 0) : (budgetWithoutSoftCost ?? 0)) / area
            : 0;
          return value !== undefined ? formatCurrency(value.toString(), true, true) : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'costPerUnit',
        header: 'Cost per Unit',
        size: 110,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const units = row.original.comp.unitCount ?? 1;
          const budgetWithSoftCost = row.original.comp.totalBudgetWithSoftCost;
          const budgetWithoutSoftCost = row.original.comp.totalBudgetWithoutSoftCost;
          const value = row.original.comp.unitCount
            ? (includeSoftCost ? (budgetWithSoftCost ?? 0) : (budgetWithoutSoftCost ?? 0)) / units
            : 0;
          return value !== undefined ? formatCurrency(value.toString(), true, true) : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'costPerNSF',
        header: 'Cost per NSF',
        size: 110,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const area = row.original.comp.netArea ?? 1;
          const budgetWithSoftCost = row.original.comp.totalBudgetWithSoftCost;
          const budgetWithoutSoftCost = row.original.comp.totalBudgetWithoutSoftCost;
          const value = row.original.comp.netArea ? (includeSoftCost ? (budgetWithSoftCost ?? 0) : (budgetWithoutSoftCost ?? 0)) / area : 0;
          return value !== undefined ? formatCurrency(value.toString(), true, true) : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'comp.grossBuildingArea',
        header: 'Gross Area (SF)',
        size: 120,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const value = row.original.comp.grossBuildingArea;
          return value !== undefined ? format(value.toString(), 'number') : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'comp.netArea',
        header: 'Net Area (SF)',
        size: 120,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const value = row.original.comp.netArea;
          return value !== undefined ? format(value.toString(), 'number') : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'comp.unitCount',
        header: 'Unit Count',
        size: 100,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => {
          const value = row.original.comp.unitCount;
          return value !== undefined ? format(value.toString(), 'number') : '--';
        },
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      {
        accessorKey: 'comp.projectAddress.city',
        header: 'City',
        size: 100,
        Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => capitalize(row.original.comp.projectAddress?.city ?? '--'),
        enableSorting: false,
        muiTableHeadCellProps: {
          align: 'right' as const,
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
        },
      },
      ...(typeofReport === ReportTypes.CC
        ? [
            {
              accessorKey: 'actions',
              header: '',
              size: 80,
              enableSorting: false,
              enableColumnFilter: false,
              enablePinning: true, // Pin this column to the right
              Cell: ({ row }: { row: MRT_Row<CCReportComp> }) => (
                <Box margin={'auto'}>
                  <WhiteFab
                    size='small'
                    aria-label='delete'
                    icon={<DeleteIcon fontSize='small' />}
                    loading={isDeleting && deletingRowId === row.original.comp.id}
                    textColor='error'
                    disabled={isDeleting}
                    onClick={async (e: any) => {
                      e.stopPropagation();
                      setDeletingRowId(row.original.comp.id);
                      await handleDelete(row.original);
                      setDeletingRowId(null);
                    }}
                  />
                </Box>
              ),
              muiTableHeadCellProps: {
                align: 'center' as const,
              },
              muiTableBodyCellProps: {
                align: 'center' as const,
              },
            },
          ]
        : []),
    ],
    [handleIncludedCompsChange, includeSoftCost, typeofReport, isDeleting, deletingRowId, handleDelete],
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredComps,
    initialState: {
      columnPinning: { left: ['isIncluded', 'comp.projectTitle'], right: ['actions'] },
      density: 'compact',
      columnVisibility,
    },
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnResizing: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableTopToolbar: true,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableSorting: false,
    enableColumnPinning: false, // Disable column pinning to remove from menu
    getRowId: (row: CCReportComp) => row.comp.id,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        margin: '0px', // Remove all margins including top margin
        maxHeight: showMap ? HEIGHTS.MAX_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT : '400px', // Set max height for scrolling
        minHeight: HEIGHTS.MIN_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT,
        width: '100%', // Use full container width
        maxWidth: '100%', // Don't exceed container
        overflow: 'auto', // Enable scrolling
        // Ensure proper scrolling behavior
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'fixed', // Use fixed table layout for better width control
        },
        // Ensure the table doesn't overflow
        '& .MuiTableContainer-root': {
          width: '100%',
          maxWidth: '100%',
        },
        // Override MRT's pinned cell alignment to center
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
      },
    },
    muiTableProps: {
      sx: {
        width: '100%',
        tableLayout: 'fixed', // Use fixed table layout
        marginTop: '0px',
      },
    },
    muiTableBodyProps: {
      sx: {
        maxHeight: showMap ? HEIGHTS.MAX_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT : '400px',
        overflow: 'auto',
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '8px 9px',
        border: 'none',
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          justifyContent: 'flex-end',
        },
      }),
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        '&[data-column-id="actions"]': {
          textAlign: 'center !important',
        },
        // Right-align numeric columns
        '&[data-column-id="totalBudget"], &[data-column-id="costPerGSF"], &[data-column-id="costPerUnit"], &[data-column-id="costPerNSF"], &[data-column-id="comp.grossBuildingArea"], &[data-column-id="comp.netArea"], &[data-column-id="comp.unitCount"]':
          {
            textAlign: 'right !important',
          },
        // Left-align text columns
        '&[data-column-id="comp.projectTitle"], &[data-column-id="comp.projectAddress.city"]': {
          textAlign: 'left !important',
        },
        // Center the selected column
        '&[data-column-id="isIncluded"]': {
          textAlign: 'center !important',
        },
      },
    },
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<CCReportComp> }) => {
      const rowIndex = row.index;
      const isEvenRow = rowIndex % 2 === 0;
      const backgroundColor = isEvenRow ? '#f9f9f9' : '#ffffff';

      return {
        sx: {
          height: '50px',
          border: 'none',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: '#f5f5f5',
            // Update pinned cell background on hover
            '& td[data-pinned="true"]::before': {
              backgroundColor: '#f5f5f5',
            },
            // Update all cells background on hover
            '& td': {
              backgroundColor: '#f5f5f5',
            },
          },
          // Also apply the same background to sticky pseudo-elements in this row
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
          // Apply the row background color to all cells in this row
          '& td': {
            backgroundColor: backgroundColor,
          },
        },
      };
    },
    muiBottomToolbarProps: {
      sx: {
        display: 'none',
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        justifyContent: 'flex-end !important',
        display: 'none',
        padding: '8px 16px',
        '& .MuiToolbar-root': {
          minHeight: 'auto',
        },
        // Hide the column actions button specifically
        '& .MuiButton-root[aria-label*="column"]': {
          display: 'none !important',
        },
        '& .MuiButton-root[aria-label*="Column"]': {
          display: 'none !important',
        },
      },
    },
  });

  useEffect(() => {
    if (onTableReady && table) {
      onTableReady(table, columnVisibility);
    }
  }, [table, onTableReady, columnVisibility]);

  useEffect(() => {
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(columnVisibility);
    }
  }, [columnVisibility, onColumnVisibilityChange]);

  return (
    <Stack
      height={showMap ? HEIGHTS.MAX_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT : '100%'}
      maxHeight={HEIGHTS.MAX_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT}
      minHeight={HEIGHTS.MIN_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT}
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden', // Prevent any overflow
      }}
    >
      <MaterialReactTable table={table} />
    </Stack>
  );
};

export default EditCCCompsSection;
